import { NgModule, Injector, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { createCustomElement } from '@angular/elements';

import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { LetModule, PushModule } from '@ngrx/component';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { QuillModule  } from 'ngx-quill'
import Counter from './features//counter';

import { EventEffects } from 'app/event/features/event.effects';
import { reducer, eventFeatureKey  } from 'app/event/features/event.reducer';

import { CoreModule } from 'app/core/core.module';
import { CampaignModule } from 'app/campaign/campaign.module'
import { DialogModule } from 'app/dialog/dialog.module';
import { ToastModule } from 'app/toast/toast.module';
import { NgxPaginationModule } from 'ngx-pagination';

import { IconModule } from '../icon/icon.module';

import {ClipboardModule} from '@angular/cdk/clipboard';
import {LayoutModule} from '@angular/cdk/layout';
import {ScrollingModule} from '@angular/cdk/scrolling';
import { MatButtonModule } from '@angular/material/button';
import { ChatModule } from '../chat/chat.module';
import { AngularCropperjsModule } from 'angular-cropperjs';
import { DragDropModule } from '@angular/cdk/drag-drop';

import { NgxCurrencyInputMode, provideEnvironmentNgxCurrency, NgxCurrencyDirective } from "ngx-currency";
import { EventListComponent } from './components/event-list/event-list.component';
import { EventTypeComponent } from './components/event-type/event-type.component';
import { InPersonEventComponent } from './components/in-person-event/in-person-event.component';
import { VirtualEventComponent } from './components/virtual-event/virtual-event.component';
import { VirtualEventSlideListComponent } from './components/virtual-event-slide-list/virtual-event-slide-list.component';
import { VirtualEventSponsorListComponent } from './components/virtual-event-sponsor-list/virtual-event-sponsor-list.component';
import { HybridEventComponent } from './components/hybrid-event/hybrid-event.component';
import { SimpleTextPartComponent } from './components/simple-text-part/simple-text-part.component';
import { FaqPartComponent } from './components/faq-part/faq-part.component';
import { FaqListComponent } from './components/faq-list/faq-list.component';
import { EventPageSlidesListComponent } from './components/event-page-slides-list/event-page-slides-list.component';
import { RegistrationListComponent } from './components/registration-list/registration-list.component';
import { EventConfigurationListComponent } from './components/event-configuration-list/event-configuration-list.component';
import { EventVenueComponent } from './components/event-venue/event-venue.component';
import { GoogleMapsAutocompleteModule } from 'app/google-maps-autocomplete/google-maps-autocomplete.module';
import { MdbPopoverModule } from 'mdb-angular-ui-kit/popover';
import { MdbDragAndDropModule } from 'mdb-angular-drag-and-drop';
import { MdbFormsModule } from 'mdb-angular-ui-kit/forms';
import { MdbSelectModule } from 'mdb-angular-ui-kit/select';
import { MdbAccordionModule } from 'mdb-angular-ui-kit/accordion';
import { MdbCheckboxModule } from 'mdb-angular-ui-kit/checkbox';
import { MdbTabsModule } from 'mdb-angular-ui-kit/tabs';
import { RouterModule } from '@angular/router';

import { EventPageCustomThemeConfigurationComponent } from './components/event-page-custom-theme-configuration/event-page-custom-theme-configuration.component';
import { EventPageEffects } from '../event-page/features/event-page.effects';
import { ActionsPopoverEventComponent } from './components/actions-popover-event/actions-popover-event.component';
import { SharedLinkEventPopoverComponent } from './components/shared-link-event-popover/shared-link-event-popover.component';
import { SharedRSVPLinkEventPopoverComponent } from './components/shared-rsvplink-event-popover/shared-rsvplink-event-popover.component';
import { SendInvitationEventPopoverComponent } from './components/send-invitation-event-popover/send-invitation-event-popover.component';
import { BulkCheckoutComponent } from './components/bulk-checkout/bulk-checkout.component';
import { EventCoverFeeSettingsComponent } from './components/event-cover-fee-settings/event-cover-fee-settings.component';
import { OrgEventPageSlidesComponent } from './components/org-event-page-slides/org-event-page-slides.component';
import { RegistrationButtonComponent } from './components/registration-button/registration-button.component';
import { CopyEventDialogComponent } from './components/copy-event-dialog/copy-event-dialog.component';
import { SimpleAudianceMonitorDialogComponent } from './components/simple-audiance-monitor-dialog/simple-audiance-monitor-dialog.component';
import { MdbInputGoogleMapsAutocompleteComponent } from 'app/core/components/mdb-input-google-maps-autocomplete/mdb-input-google-maps-autocomplete.component';

@NgModule({
  declarations: [
    EventListComponent,
    EventTypeComponent,
    InPersonEventComponent,
    VirtualEventComponent,
    VirtualEventSlideListComponent,
    VirtualEventSponsorListComponent,
    HybridEventComponent,
    EventConfigurationListComponent,
    SimpleTextPartComponent,
    FaqPartComponent,
    FaqListComponent,
    EventPageSlidesListComponent,
    RegistrationListComponent,
    RegistrationButtonComponent,
    EventVenueComponent,
    EventPageCustomThemeConfigurationComponent,
    ActionsPopoverEventComponent,
    SharedLinkEventPopoverComponent,
    SharedRSVPLinkEventPopoverComponent,
    SendInvitationEventPopoverComponent,
    BulkCheckoutComponent,
    EventCoverFeeSettingsComponent,
    OrgEventPageSlidesComponent,
    CopyEventDialogComponent,
    SimpleAudianceMonitorDialogComponent
  ],
  providers: [provideEnvironmentNgxCurrency({
    align: "left",
    allowNegative: false,
    allowZero: true,
    decimal: ".",
    precision: 0,
    prefix: "$ ",
    suffix: "",
    thousands: ",",
    nullable: true,
    min: null,
    max: null,
    inputMode: NgxCurrencyInputMode.Financial
  })],
  imports: [
    CommonModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    LetModule,
    PushModule,
    StoreModule.forRoot({}, {
      runtimeChecks: {
        strictStateImmutability: false,
        strictActionImmutability: false,
      },
    }),
    StoreModule.forFeature(eventFeatureKey, reducer),
    EffectsModule.forRoot([]),
    EffectsModule.forFeature([EventEffects, EventPageEffects]),
    StoreDevtoolsModule.instrument({
      maxAge: 25, // Retains last 25 states
    }),
    CoreModule,
    CampaignModule,
    DialogModule,
    ToastModule,
    IconModule,
    ClipboardModule,
    LayoutModule,
    ScrollingModule,
    MatButtonModule,
    NgScrollbarModule,
    AngularCropperjsModule,
    ChatModule,
    DragDropModule,
    MdbPopoverModule,
    GoogleMapsAutocompleteModule,
    MdbDragAndDropModule,
    MdbFormsModule,
    MdbSelectModule,
    MdbAccordionModule,
    MdbCheckboxModule,
    MdbTabsModule,
    NgxPaginationModule,
    RouterModule,
    QuillModule.forRoot({
      customModules: [{
        implementation: Counter,
        path: 'modules/counter'
      }]
    }),
    NgxCurrencyDirective,
    MdbInputGoogleMapsAutocompleteComponent,
  ],
  exports: [CopyEventDialogComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class EventModule {
  constructor(injector: Injector) {
    const BulkCheckoutElement = createCustomElement(BulkCheckoutComponent, {injector});

    try {
      customElements.define('bulk-checkout-element', BulkCheckoutElement);
    } catch(e) {}
  }
}
